import React, { useMemo } from 'react';
import Table from 'Components/DesignSystem/Table/Table';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Tag from 'Components/DesignSystem/Tag/Tag';
import { TableColumn } from 'types/DesignSystem/Table';

type TargetUniqueAttributeTableProps = {
    payload: TRecategorizePayload;
    recategorizationResponse: RecategorizationResponse | undefined;
}

/**
 * Step 2, third tab — the TARGET category's unique attributes, read-only.
 *
 * On DS `Table` since 2026-09-13. It was a hand-rolled `<table>` with its own zebra,
 * its own `#F4F5F7` head and its own `#DADBDD` rules; all three are the DS's own
 * defaults now (`headerVariant` follows the zebra per § 245).
 *
 * **The row checkbox is decorative and must stay that way** — `checked` and
 * `disabled`, wired to nothing. It says "this target attribute is included", which is
 * not a choice the user has here; the choices are made on the Source tab. Pinned.
 */
const TargetUniqueAttributeTable = ({ payload, recategorizationResponse }: TargetUniqueAttributeTableProps) => {

    const getMappedSourceAttribute = (targetAttributeId: string) => {
        return payload.unique_attr_source_list.find(
            attr => attr.target_attribute_id === targetAttributeId && attr.checked
        );
    };

    const rows = recategorizationResponse?.unique_attr_target_list ?? [];

    const columns = useMemo<TableColumn<Attribute>[]>(() => [
        {
            id: 'selected',
            header: '',
            accessor: 'attribute_id',
            width: '10%',
            cellType: 'custom',
            render: () => (
                <Checkbox
                    checked
                    disabled
                    onChange={() => undefined}
                    data-testid="recategorize-target-row-checkbox"
                    aria-label="Included in the target category"
                />
            ),
        },
        {
            id: 'source',
            header: payload.source_category_name ?? '',
            accessor: (row: Attribute) => row.attribute_id,
            width: '30%',
            render: (_v, row) => getMappedSourceAttribute(row.attribute_id)?.attribute_name ?? 'NA',
        },
        {
            id: 'action',
            header: 'Performed Action',
            accessor: (row: Attribute) => row.attribute_id,
            width: '25%',
            cellType: 'tag',
            render: (_v, row) => {
                const mapped = getMappedSourceAttribute(row.attribute_id);
                return mapped?.action ? <Tag label={mapped.action} variant="meta" /> : null;
            },
        },
        {
            id: 'target',
            header: payload.target_category_name ?? '',
            accessor: 'attribute_name',
            width: '35%',
        },
    ], [payload.source_category_name, payload.target_category_name, payload.unique_attr_source_list]);

    return (
        <div className="tw-w-full tw-max-w-4xl tw-mx-auto tw-mt-3">
            <Table<Attribute>
                columns={columns}
                data={rows}
                rowKey="attribute_id"
                striped
                bordered
                data-testid="recategorize-target-attributes-table"
                emptyContent={null}
            />
        </div>
    );
};

export default TargetUniqueAttributeTable;
